import { motion } from "framer-motion";
import { MapPin, Eye, Heart, BadgeCheck, ArrowUpRight, Images } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import AnimatedPrice from "@/components/AnimatedPrice";

export default function ListingCard({ listing, onOpen, onToggleFav, index = 0 }) {
  const { currency, format } = useCurrency();
  const img = listing.images?.[0] ? fileUrl(listing.images[0]) : null;
  const showOriginal = listing.currency && listing.currency !== currency;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.05, 0.4), type: "spring", stiffness: 260, damping: 26 }}
      className="goe-card lift pressable cursor-pointer overflow-hidden"
      onClick={() => onOpen?.(listing)}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="relative">
        {img ? (
          <img src={img} alt={listing.title} loading="lazy"
               className="h-40 w-full object-cover" />
        ) : (
          <div className="skeleton h-40 w-full" style={{ borderRadius: 0 }} />
        )}
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-2 pt-8"
             style={{ background: "linear-gradient(to top, rgba(5,6,9,0.85), transparent)" }}>
          <button onClick={(e) => { e.stopPropagation(); onOpen?.(listing); }} data-testid={`view-full-${listing.id}`}
                  className="pressable invisible flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-extrabold text-black transition-transform hover:scale-105"
                  style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}>
            View full listing <ArrowUpRight size={12} strokeWidth={3} />
          </button>
          {listing.images?.length > 1 && (
            <span className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-bold text-white">
              <Images size={11} />{listing.images.length}
            </span>
          )}
        </div>
        <span
          className="absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold text-white"
          style={{ background: listing.status === "sold" ? "var(--red)" : "var(--green)" }}
        >
          {listing.status === "sold" ? "SOLD" : "AVAILABLE"}
        </span>
        {onToggleFav && (
          <button
            data-testid={`fav-btn-${listing.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFav(listing); }}
            className="pressable glass absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full"
          >
            <Heart size={18} fill={listing.is_favorite ? "#F59E0B" : "transparent"}
                   color={listing.is_favorite ? "#F59E0B" : "#fff"} />
          </button>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display line-clamp-1 text-[15px] font-700" style={{ fontWeight: 700 }}>
            {listing.title}
          </h3>
          <AnimatedPrice amount={listing.price} from={listing.currency || "USD"}
                         className="accent shrink-0 text-[15px] font-extrabold" />
        </div>
        {showOriginal && (
          <div className="mt-0.5 text-right text-[11px] text-dim">
            {format(listing.price, listing.currency, { from: listing.currency })} original
          </div>
        )}
        <p className="mt-1 text-sm text-dim">
          {listing.commodity}{listing.quantity ? ` · ${listing.quantity}` : ""}
        </p>
        <div className="mt-2 flex items-center justify-between text-xs text-dim">
          <span className="flex items-center gap-1"><MapPin size={13} />{listing.location || "—"}</span>
          <span className="flex items-center gap-1"><Eye size={13} />{listing.views || 0}</span>
        </div>
        {listing.seller && (
          <div className="mt-2 flex items-center gap-2 border-t pt-2" style={{ borderColor: "var(--border)" }}>
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-white overflow-hidden">
              {listing.seller.avatar
                ? <img src={fileUrl(listing.seller.avatar)} alt="" className="h-full w-full object-cover" />
                : (listing.seller.name?.[0] || "?").toUpperCase()}
            </div>
            <span className="line-clamp-1 text-xs font-medium">{listing.seller.name}</span>
            {listing.seller.verified && <BadgeCheck size={13} className="accent" />}
          </div>
        )}
      </div>
    </motion.div>
  );
}
